import axios from 'axios';
import { API_BASE_URL, DEVICE_PLATFORM } from '@/config/constants';
import useAuthStore from '@/stores/authStore';
import { getRecaptchaToken, getActionForEndpoint, isProtectedEndpoint } from '@/utils/recaptcha';
import { showErrorToast } from '@/utils/toastManager';
import { getApiErrorMessage, shouldShowErrorToast } from '@/utils/errorUtils';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Device-Platform': DEVICE_PLATFORM,
    'Accept-Language': 'ja-JP',
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  async (config) => {
    // Skip authorization header for auth endpoints
    if (config.url.includes('/auth/login') ||
        config.url.includes('/auth/signup') ||
        config.url.includes('/auth/token/refresh')) {
      config.headers.Authorization = null;
    } else {
      const token = useAuthStore.getState().accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Add reCAPTCHA token for protected endpoints
    if (isProtectedEndpoint(config.url)) {
      try {
        const action = getActionForEndpoint(config.url);
        if (action) {
          console.log(`Getting reCAPTCHA token for ${config.url} with action: ${action}`);
          // Don't show badge for automatic token generation
          const recaptchaToken = await getRecaptchaToken(action, false);
          config.headers['Recaptcha-Token'] = recaptchaToken;
          console.log(`reCAPTCHA token obtained successfully for ${action}`);
        }
      } catch (error) {
        console.error(`Failed to get reCAPTCHA token for ${config.url}:`, error);
        // Continue with request without reCAPTCHA token
        // The server will handle the missing token appropriately
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle reCAPTCHA verification failure (400 error on protected endpoints)
    if (error.response?.status === 400 &&
        isProtectedEndpoint(originalRequest.url) &&
        !originalRequest._recaptchaRetry) {

      console.log(`reCAPTCHA verification may have failed for ${originalRequest.url}, retrying with new token...`);
      originalRequest._recaptchaRetry = true;

      try {
        const action = getActionForEndpoint(originalRequest.url);
        if (action) {
          console.log(`Retrying with fresh reCAPTCHA token for action: ${action}`);
          // Don't show badge for retry attempts either
          const newRecaptchaToken = await getRecaptchaToken(action, false);
          originalRequest.headers['Recaptcha-Token'] = newRecaptchaToken;
          console.log(`Retry successful, making request again for ${originalRequest.url}`);
          return axiosInstance(originalRequest);
        }
      } catch (recaptchaError) {
        console.error(`Failed to retry with new reCAPTCHA token for ${originalRequest.url}:`, recaptchaError);
        // Fall through to original error handling
      }
    }

    // Handle 401 unauthorized (token refresh)
    if (error.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url.includes('/auth/login') &&
        !originalRequest.url.includes('/auth/token/refresh')) {

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { refreshToken } = await import('./auth');
        const response = await refreshToken();
        const { accessToken } = response;

        useAuthStore.getState().setAccessToken(accessToken);

        // Update authorization header
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        processQueue(null, accessToken);

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        useAuthStore.getState().clearAuth();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Show general error toast for unhandled errors
    if (shouldShowErrorToast(error)) {
      const errorMessage = getApiErrorMessage(error);
      if (errorMessage) {
        showErrorToast(errorMessage);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;